import { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { PeriodTabs } from '../components/PeriodTabs';
import { RaporTablosu } from '../components/rapor-ekrani/RaporTablosu';
import { RaporHatasi } from '../components/rapor-ekrani/RaporHatasi';
import { FILTRE_GIRIS, FiltreAlani, RaporFiltreBari, gecerliTarih } from '../components/rapor-ekrani/RaporFiltreBari';
import { useCekmeceSecimi } from '../components/rapor-ekrani/useCekmeceSecimi';
import { ekranKaynagi, useRaporSorgu } from '../components/rapor-ekrani/useRaporSorgu';
import { useDarEkran } from '../components/rapor-ekrani/useDarEkran';
import { paraMetni, sayiMetni } from '../components/rapor-ekrani/bicim';
import type { GrupAyari, RaporKolonu, Siralama } from '../components/rapor-ekrani/kolon';
import { AdisyonCekmecesi } from '../components/hamam-performans/AdisyonCekmecesi';
import { CiroCubuklari } from '../components/hamam-performans/CiroCubuklari';
import { TarafKarti } from '../components/hamam-performans/TarafKarti';
import { Kart, Kutu, Rozet, sureHucresi } from '../components/hamam-performans/gorsel';
import {
  BOLUM_SECENEKLERI, DONEM_SECENEKLERI, TARAF_SECENEKLERI,
  aralikMetni, bolumeGoreSuz, donemAraligi, gunSaatMetni, hedefCoz, kabinSatiri, olcu, payMetni, satilanUrunler,
  sureMetni, tarafaGoreSuz, tarafOzeti,
  type BolumSecimi, type Cins, type Donem, type HamOzetSatiri, type HamSatilanSatiri, type KabinSatiri, type Taraf,
  type UrunSatiri,
} from '../components/hamam-performans/tipler';
import type { EkranProps } from '../ekranKayitDefteri';

const VARSAYILAN_DONEM: Donem = 'son7';
/** Kabin tablosu ve satılanlar çok satandan aza açılır. */
const TUTARA_GORE: Siralama = { id: 'tutar', yon: -1 };
const URUN_GRUBU: GrupAyari<UrunSatiri> = { id: 'grup', deger: (u) => u.grup, birim: 'ürün' };

/**
 * S340: Osmanlı Hamamı "Performans" ekranı (onaylı demo `tasks/hamam-performans/sablon.html`). Seçilen
 * aralıkta KAPANAN adisyonlar, kapanış gününe göre. Kaynaklar `firm_panel_screens.config.kaynaklar`
 * sözlüğünden çözülür: `ozet`, `zaman`, `adisyonlar`, `kalemler`, `satilan`.
 *
 * Özet kutuları, taraf kartları ve kabin tablosu TEK sonuçtan (`ozet`: satır = kabin / kasa) istemcide
 * toplanır; üçü aynı sayıyı gösterir, biri ötekinden ayrışamaz. Taraf seçicisi (Tümü / Bayan / Erkek) ve
 * bölüm seçicisi İSTEMCİDE süzer (sorgu yinelenmez); tarih değişince sorgular yinelenir ve önceki veri o
 * arada ekranda kalır. Çekmecedeki kabin URL'de durur (`?kabin=<cins|bolum|kabin>`).
 *
 * TELEFON (<= 767 px): kutular ikişer, taraf kartları alt alta, tablolar KART kipinde, çekmece tam ekran.
 * Yapışık barda yalnız dönem seçicisi durur; tarihler ve taraf katlanır "Filtreler"in içinde.
 */
export default function HamamPerformans({ screen }: EkranProps) {
  const ozetKaynagi = ekranKaynagi(screen, 'ozet');
  const satilanKaynagi = ekranKaynagi(screen, 'satilan');
  const telefon = useDarEkran();

  const [bugun] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [donem, setDonem] = useState<Donem>(VARSAYILAN_DONEM);
  // Tarihler iki durumdur: `...Giris` alanda yazan, `bas` / `bit` sorguya giden. Elle yazarken ara değerler
  // ("0002-10-01") sorgu anahtarına girmez; ekran boşalmaz, gereksiz istek gitmez.
  const [aralik, setAralik] = useState(() => donemAraligi(VARSAYILAN_DONEM, bugun)!);
  const [giris, setGiris] = useState(aralik);
  const { bas, bit } = aralik;
  const aralikUygula = useCallback((yeni: { bas: string; bit: string }, yeniDonem: Donem) => {
    setDonem(yeniDonem);
    setAralik(yeni);
    setGiris(yeni);
  }, []);
  const donemSec = (d: Donem) => {
    const a = donemAraligi(d, bugun);
    // "Özel": tarihler olduğu gibi kalır, alanlardan değiştirilir.
    if (a) aralikUygula(a, d);
    else setDonem(d);
  };
  const tarihDegis = (alan: 'bas' | 'bit', v: string) => {
    setGiris((g) => ({ ...g, [alan]: v }));
    if (!gecerliTarih(v)) return;
    // Başlangıç bitişi geçerse öteki uç aynı güne çekilir (ters aralık sorguya gitmez).
    const yeni = alan === 'bas' ? { bas: v, bit: v > bit ? v : bit } : { bas: v < bas ? v : bas, bit: v };
    aralikUygula(yeni, 'ozel');
  };
  const gunSec = useCallback((gun: string) => aralikUygula({ bas: gun, bit: gun }, 'ozel'), [aralikUygula]);

  const [taraf, setTaraf] = useState<Taraf>('T');
  const [bolum, setBolum] = useState<BolumSecimi>('T');
  const [kabinSiralama, setKabinSiralama] = useState<Siralama | null>(TUTARA_GORE);
  const [urunSiralama, setUrunSiralama] = useState<Siralama | null>(TUTARA_GORE);

  const tarihler = { '@BASTARIH': bas, '@BITTARIH': bit };
  const q = useRaporSorgu<HamOzetSatiri>(ozetKaynagi, tarihler);
  const satilanQ = useRaporSorgu<HamSatilanSatiri>(satilanKaynagi, tarihler);

  const tum = useMemo(() => (q.data ?? []).map(kabinSatiri), [q.data]);
  const suzulen = useMemo(() => tarafaGoreSuz(tum, taraf), [tum, taraf]);
  const toplam = useMemo(() => olcu(suzulen), [suzulen]);
  const taraflar = useMemo(
    () => (taraf === 'T' ? (['K', 'E'] as Cins[]) : [taraf]).map((c) => tarafOzeti(suzulen, c)),
    [suzulen, taraf],
  );
  const kabinsiz = useMemo(() => olcu(suzulen.filter((s) => s.cins === '-')), [suzulen]);
  const kabinler = useMemo(() => bolumeGoreSuz(suzulen, bolum), [suzulen, bolum]);

  const urunler = useMemo(() => satilanUrunler(satilanQ.data ?? [], taraf), [satilanQ.data, taraf]);
  const urunToplami = useMemo(() => urunler.reduce((t, u) => t + u.tutar, 0), [urunler]);

  const secim = useCekmeceSecimi('kabin');
  const hedef = useMemo(() => hedefCoz(secim.secili, tum), [secim.secili, tum]);
  const { ac } = secim;
  const kabinTiklandi = useCallback((k: KabinSatiri) => ac(k.kimlik), [ac]);

  const kabinKolonlari = useMemo<RaporKolonu<KabinSatiri>[]>(() => {
    // Rozet satırdan çizilir; hücre çizicisi yalnız DEĞERİ alır, satır etiketinden bulunur.
    const etiketten = new Map(kabinler.map((k) => [k.etiket, k]));
    // `kart`: telefonda kabin kartı. Üstte kabin, sağında ciro; altında adisyon, son kapanış ve sağında
    // ortalama kalış; en altta nakit / kart.
    return [
      {
        id: 'kabin', baslik: 'Kabin / kasa', tur: 'metin', esnek: true, kart: 'baslik',
        deger: (k) => k.etiket,
        hucre: (v) => {
          const k = etiketten.get(String(v));
          return k ? <><Rozet cins={k.cins} /> {k.ad}</> : null;
        },
      },
      { id: 'adisyon', baslik: 'Adisyon', tur: 'sayi', deger: (k) => k.adisyon, toplam: true, kart: 'alt', kartEtiketi: 'Adisyon' },
      { id: 'tutar', baslik: 'Ciro', tur: 'para', deger: (k) => k.tutar, toplam: true, kart: 'olcu' },
      { id: 'nakit', baslik: 'Nakit', tur: 'para', deger: (k) => k.nakit, toplam: true, kart: 'ikincil' },
      { id: 'kart', baslik: 'Kart', tur: 'para', deger: (k) => k.kart, toplam: true, kart: 'ikincil' },
      {
        id: 'kalis', baslik: 'Ort. kalış', tur: 'sayi', genislik: 96, kart: 'olcuAlt', kartEtiketi: 'Ort. kalış',
        deger: (k) => k.ortKalis,
        // Toplamdaki kalış kabin ortalamalarının ortalaması DEĞİL: Σ dakika / Σ adisyon.
        toplam: (s) => olcu(s).kalis,
        hucre: sureHucresi,
      },
      {
        id: 'son', baslik: 'Son kapanış', tur: 'metin', hizala: 'sag', genislik: 112, kart: 'alt', kartEtiketi: 'Son',
        // Değer zaman damgasının tamamıdır (sıralama doğru çıksın); hücre "03.10 14:25" yazar.
        deger: (k) => k.sonKapanis,
        hucre: (v) => gunSaatMetni(typeof v === 'string' ? v : null),
      },
    ];
  }, [kabinler]);

  const urunKolonlari = useMemo<RaporKolonu<UrunSatiri>[]>(() => {
    const pay = (tutar: number) => (urunToplami ? (tutar / urunToplami) * 100 : null);
    return [
      { id: 'urun', baslik: 'Grup / ürün', tur: 'metin', deger: (u) => u.urun, esnek: true, kart: 'baslik' },
      { id: 'adet', baslik: 'Adet', tur: 'sayi', deger: (u) => u.adet, toplam: true, kart: 'alt', kartEtiketi: 'Adet' },
      { id: 'tutar', baslik: 'Tutar', tur: 'para', deger: (u) => u.tutar, toplam: true, kart: 'olcu' },
      {
        id: 'pay', baslik: 'Pay', tur: 'yuzde', kart: 'olcuAlt', kartEtiketi: 'Pay',
        deger: (u) => pay(u.tutar),
        // Pay, payların toplamı değil: grubun (ya da tümünün) tutarı / gösterilen toplam tutar.
        toplam: (s) => pay(s.reduce((t, u) => t + u.tutar, 0)),
        hucre: (v) => (typeof v === 'number' ? `%${Math.round(v)}` : ''),
      },
      { id: 'bayan', baslik: 'Bayan', tur: 'para', deger: (u) => u.bayan, toplam: true, kart: 'ikincil' },
      { id: 'erkek', baslik: 'Erkek', tur: 'para', deger: (u) => u.erkek, toplam: true, kart: 'ikincil' },
    ];
  }, [urunToplami]);

  const ilkYukleme = q.isLoading;
  const yenileniyor = q.isFetching && !ilkYukleme;
  const kutu = (deger: string) => (ilkYukleme ? '…' : deger);
  const bilgi = `${sayiMetni(toplam.adisyon)} adisyon · ${aralikMetni(bas, bit)}`;

  const tarihAlani = (etiket: string, alan: 'bas' | 'bit') => (
    <FiltreAlani etiket={etiket}>
      <input
        type="date"
        className={FILTRE_GIRIS}
        value={giris[alan]}
        onChange={(e) => tarihDegis(alan, e.target.value)}
        onBlur={() => { if (!gecerliTarih(giris[alan])) setGiris(aralik); }}
      />
    </FiltreAlani>
  );

  return (
    <div data-testid="hamam-performans" className="flex flex-col gap-3">
      <RaporFiltreBari
        testid="hamam-filtre-bar"
        birincil={
          <FiltreAlani etiket="Dönem" grup>
            <PeriodTabs value={donem} onChange={donemSec} secenekler={DONEM_SECENEKLERI} kompakt />
          </FiltreAlani>
        }
        // Taraf sonucu daraltır: kapalı panelde bu görünmeli.
        etkinAdlar={taraf !== 'T' ? ['Taraf'] : []}
        ozet={ilkYukleme || !ozetKaynagi ? undefined : yenileniyor ? 'Yenileniyor…' : bilgi}
      >
        {tarihAlani('Başlangıç', 'bas')}
        {tarihAlani('Bitiş', 'bit')}
        <FiltreAlani etiket="Taraf" grup>
          <PeriodTabs value={taraf} onChange={setTaraf} secenekler={TARAF_SECENEKLERI} kompakt />
        </FiltreAlani>
      </RaporFiltreBari>

      {/* Telefonda bu satır yok: aynı bilgi "Filtreler" başlığının özetinde (sayı ekranda tek yerde). */}
      {!telefon && (
        <div className="text-[13px] text-ink-500 min-h-[20px]" data-testid="hamam-bilgi">
          {ilkYukleme || !ozetKaynagi ? (
            <>&nbsp;</>
          ) : (
            <>
              <b className="font-semibold text-ink-900">{sayiMetni(toplam.adisyon)}</b> adisyon · {aralikMetni(bas, bit)} · kapanış gününe göre
              {yenileniyor && <span className="ml-2 text-ink-400" role="status">Yenileniyor…</span>}
            </>
          )}
        </div>
      )}

      {!ozetKaynagi ? (
        <RaporHatasi testid="hamam-kaynaksiz" mesaj="Bu firmada performans özeti kaynağı tanımlı değil." />
      ) : q.isError ? (
        <RaporHatasi testid="hamam-hata" mesaj="Performans özeti yüklenemedi." onTekrar={() => q.refetch()} />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="hamam-ozet" aria-busy={q.isFetching}>
            <Kutu etiket="Ciro" alt={`adisyon başına ${toplam.adisyon ? paraMetni(toplam.tutar / toplam.adisyon) : '-'}`}>{kutu(paraMetni(toplam.tutar))}</Kutu>
            <Kutu etiket="Nakit" alt={`${payMetni(toplam.nakit, toplam.tutar)} pay`}>{kutu(paraMetni(toplam.nakit))}</Kutu>
            <Kutu etiket="Kart" alt={`${payMetni(toplam.kart, toplam.tutar)} pay`}>{kutu(paraMetni(toplam.kart))}</Kutu>
            <Kutu etiket="Adisyon" alt="kapanan">{kutu(sayiMetni(toplam.adisyon))}</Kutu>
            <Kutu etiket="Yıkama" alt="satılan yıkama ücreti">{kutu(sayiMetni(toplam.yikama))}</Kutu>
            <Kutu etiket="Ortalama kalış" alt="Salon ve VİP kabinleri">{kutu(sureMetni(toplam.kalis))}</Kutu>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="hamam-taraflar">
            {taraflar.map((t) => (
              <TarafKarti
                key={t.cins}
                ozet={t}
                onBolumAc={ac}
                seciliKimlik={hedef?.kimlik ?? null}
                yukleniyor={ilkYukleme}
                yenileniyor={yenileniyor}
              />
            ))}
            {taraf === 'T' && kabinsiz.adisyon > 0 && (
              <p className="m-0 md:col-span-2 text-[13px] text-ink-500" data-testid="hamam-kabinsiz-notu">
                Kabine bağlı olmayan satış: {sayiMetni(kabinsiz.adisyon)} adisyon, {paraMetni(kabinsiz.tutar)} (nakit{' '}
                {paraMetni(kabinsiz.nakit)}, kart {paraMetni(kabinsiz.kart)}). Kabin tablosunda ayrı satırdadır.
              </p>
            )}
          </div>
        </>
      )}

      <CiroCubuklari kaynak={ekranKaynagi(screen, 'zaman')} bas={bas} bit={bit} taraf={taraf} onGunSec={gunSec} />

      {ozetKaynagi && !q.isError && (
        <Kart
          testid="hamam-kabinler"
          baslik="Kabinler ve kasalar"
          alt="Satıra basınca o kabinin adisyonları açılır"
          araclar={
            <div role="group" aria-label="Bölüm">
              <PeriodTabs value={bolum} onChange={setBolum} secenekler={BOLUM_SECENEKLERI} kompakt />
            </div>
          }
        >
          <RaporTablosu
            testid="hamam-kabin-tablosu"
            etiket="Kabinler ve kasalar"
            // Masaüstünde tablo kendi içinde kayar (başlık ve toplam yapışık); telefonda kart listesi sayfayla kayar.
            className={telefon ? '' : 'max-h-[520px]'}
            kartStili="cizgi"
            kolonlar={kabinKolonlari}
            satirlar={kabinler}
            satirKimligi={(k) => k.kimlik}
            onSatirTikla={kabinTiklandi}
            seciliKimlik={hedef?.kimlik ?? null}
            siralama={kabinSiralama}
            onSiralama={setKabinSiralama}
            toplamEtiketi={`Toplam (${sayiMetni(kabinler.length)} kabin / kasa)`}
            bosMesaj="Bu aralıkta adisyon yok"
            yukleniyor={ilkYukleme}
            yenileniyor={yenileniyor}
          />
        </Kart>
      )}

      <Kart testid="hamam-satilanlar" baslik="Satılanlar" alt="Adisyon kalemleri; gruba basınca ürünler açılır">
        {!satilanKaynagi ? (
          <RaporHatasi testid="hamam-satilan-kaynaksiz" mesaj="Bu firmada satılanlar kaynağı tanımlı değil." />
        ) : satilanQ.isError ? (
          <RaporHatasi testid="hamam-satilan-hata" mesaj="Satılanlar yüklenemedi." onTekrar={() => satilanQ.refetch()} />
        ) : (
          <RaporTablosu
            testid="hamam-satilan-tablosu"
            etiket="Satılanlar"
            kartStili="cizgi"
            kolonlar={urunKolonlari}
            satirlar={urunler}
            satirKimligi={(u) => u.kimlik}
            grup={URUN_GRUBU}
            siralama={urunSiralama}
            onSiralama={setUrunSiralama}
            toplamEtiketi="Toplam"
            bosMesaj="Bu aralıkta kalem yok"
            yukleniyor={satilanQ.isLoading}
            yenileniyor={satilanQ.isFetching && !satilanQ.isLoading}
          />
        )}
      </Kart>

      <AdisyonCekmecesi
        hedef={hedef}
        kaynak={ekranKaynagi(screen, 'adisyonlar')}
        kalemKaynagi={ekranKaynagi(screen, 'kalemler')}
        bas={bas}
        bit={bit}
        onKapat={secim.kapat}
      />
    </div>
  );
}
